import React, { useContext, useMemo } from "react";
import PropTypes from "prop-types";
import moment from "moment";

import PromotionalBetMark from "@tickets/components/promotionalBetMark";
import { isCashout } from "@tickets/helpers/actions/isActionAvailable";
import { BetStatus, DetailBetStatus } from "../components/betStatus";
import { PreferencesContext } from "@common/contexts/preferences";
import { BetStateEnum } from "@app/dev/constants/enums/betState";
import TicketSkeleton from "@common/components/ticketSkeleton";
import { useTranslation } from "@common/hooks/translation";
import OddConverter from '@common/helpers/oddConverter';
import Money from "@app/dev/terminal/components/money";
import BetType from "../components/betType";
import Config from "@app/dev/config/main";
import { BetBuilderCard, buildBetBuilderCards } from "../betBuilder";

const TicketDetailsSport = ({ ticket }) => {
  const t = useTranslation();
  const [preferences] = useContext(PreferencesContext);

  const { dataFromSwarm } = ticket;

  const ticketPrice = dataFromSwarm.price === -1 ? t("SP") : dataFromSwarm.price ?? "-";
  const winAmount = dataFromSwarm.win_amount - dataFromSwarm.tax_amount;
  const totalStake = (dataFromSwarm.stake_tax_amount || 0) + (dataFromSwarm.amount || 0) + (dataFromSwarm.free_bet_amount || 0);

  const isBanker = useMemo(() => (
    ticket.dataFromSwarm.selection_list?.some((item) => item.is_banker)
  ), [ticket]);

  // A Multiple/System can mix bet builder legs with standalone selections.
  // Legs with `bet_builder_match_id` form BB cards (grouped by that key);
  // the rest render as plain rows.
  const { betBuilderCards, standaloneSelections } = useMemo(() => {
    const selections = dataFromSwarm.selection_list || [];
    const bbLegs = selections.filter((s) => s.bet_builder_match_id != null);
    const others = selections.filter((s) => s.bet_builder_match_id == null);
    return {
      betBuilderCards: buildBetBuilderCards(bbLegs, "bet_builder_match_id"),
      standaloneSelections: others,
    };
  }, [dataFromSwarm.selection_list]);

  const isPromotionalBet = !!(dataFromSwarm.is_risk_free_bet || dataFromSwarm.free_bet_amount || dataFromSwarm.boost_percentage);
  const hasAmounts = !!(dataFromSwarm.stake_tax_amount && dataFromSwarm.amount);

  return (
    <TicketSkeleton dark>
      <div className="ticket-section">
        <div className="ticket-info-row">
          <p>{t("Status")}</p>
          <BetStatus data={ticket} />
        </div>
        <div className="ticket-info-row">
          <p>{t("Id")}</p>
          <span>{dataFromSwarm.id}</span>
        </div>
        <div className="ticket-info-row">
          <p>{t("DATE")}</p>
          <span>{moment(dataFromSwarm.created).format("DD/MM/YYYY HH:mm:ss")}</span>
        </div>
        <div className="ticket-info-row">
          <p>{t("Bet Type")}</p>

          <span className="T-sport-ticket-bet-type">
            <BetType data={ticket} />
            {isBanker && <span className="T-sport-ticket-banker">{t('Banker')}</span>}
          </span>
        </div>
      </div>

      {betBuilderCards.map((card) => (
        <BetBuilderCard key={card.matchId} card={card} />
      ))}

      {standaloneSelections.length > 0 && (
        <div className="ticket-section">
          {standaloneSelections.map((selection) => (
            <div className="ticket-nested-section" key={selection.selection_id}>
              <div className="ticket-info-row">
                <span>{selection.sport_name}</span>
              </div>
              <div className="ticket-info-row">
                {selection.is_live && <span className="live-icon-ticket">{t("Live")}</span>}
                <p className="match-name">
                  {selection.is_banker && <span className="T-sport-ticket-selection-banker">{t('banker_short_label')}</span>}
                  <span>{selection.match_name}</span>
                </p>
                <DetailBetStatus data={selection} isJustText />
              </div>
              <div className="ticket-info-row">
                <p>{t('Match Start Time')}</p>
                <p>{moment(selection.start_time).format("DD/MM/YYYY HH:mm:ss") || '-'}</p>
              </div>
              <div className="ticket-info-row">
                <p>{`${selection.display_market_name} ${selection.display_selection_name || ''}`}</p>
                <p className="ticket-info-white">{selection.price === -1 ? t("SP") : OddConverter(Config, selection.price, preferences.oddsFormat)}</p>
              </div>
              <div className="ticket-info-row">
                <p>{t("Event result")}</p>
                <p className="ticket-info-white">{selection.match_info || "-"}</p>
              </div>
            </div>
          ))}
        </div>
      )}

      <div className="ticket-section">
        <div className="ticket-info-row">
          <p>{t("Total odd")}</p>
          <p className="ticket-info-white">{OddConverter(Config, ticketPrice, preferences.oddsFormat)}</p>
        </div>
        <div className="ticket-info-row">
          <p>{t("Bet Amount")}</p>
          <p className="ticket-info-white">
            <Money amount={dataFromSwarm.amount} />
          </p>
        </div>
        {!!dataFromSwarm.stake_tax_amount && (
          <div className="ticket-info-row">
            <p>{t("Tax on stake")}</p>
            <p className="ticket-info-white">
              <Money amount={dataFromSwarm.stake_tax_amount} />
            </p>
          </div>
        )}
        {(hasAmounts || isPromotionalBet) && (
          <div className="ticket-info-row">
            <p>{t("Total stake")}</p>
            <p className="ticket-info-white">
              <Money amount={totalStake} />
              <PromotionalBetMark data={dataFromSwarm} />
            </p>
          </div>
        )}
        {dataFromSwarm.boost_percentage && (
          <div className="ticket-info-row">
            <div className="ticket-additional-info">
              <p>{t("Profit Boost:")}</p>
              <p className="ticket-percentage-info">{`${dataFromSwarm.boost_percentage}%`}</p>
            </div>
            <p className="ticket-info-white">
              <Money amount={dataFromSwarm.bonus_amount} />
            </p>
          </div>
        )}
        {!!dataFromSwarm.cashout_amount && isCashout(ticket) && (
          <div className="ticket-info-row">
            <p>{t("Available cash-out")}</p>
            <p className="ticket-info-white">
              <Money amount={dataFromSwarm.cashout_amount} />
            </p>
          </div>
        )}
        {dataFromSwarm.state_id === BetStateEnum.Accepted && (
          <div className="ticket-info-row">
            <p>{t("Possible win")}</p>
            <p className="ticket-info-white">
              {dataFromSwarm.price === -1 ? 'SP' : <Money amount={dataFromSwarm.possible_win - dataFromSwarm.possible_tax_amount} />}
            </p>
          </div>
        )}
        {dataFromSwarm.state_id !== BetStateEnum.Accepted && !!winAmount && (
          <div className="ticket-info-row">
            <p>{t("Won amount")}</p>
            <p className="ticket-info-white">
              <Money amount={winAmount} />
            </p>
          </div>
        )}
      </div>
      {dataFromSwarm.is_risk_free_bet && (
        <div className="ticket-section">
          <div className="ticket-info-row">
            <p className="risk-free-info-txt">{t('Your bet is risk free. Your bet amount will be returned in case of the bet is loss')}</p>
          </div>
        </div>
      )}
    </TicketSkeleton>
  );
};

TicketDetailsSport.propTypes = {
  ticket: PropTypes.object.isRequired,
};

export default TicketDetailsSport;
